import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { formatRelativeTime } from "../../utils";
import { highlightHtmlContent } from "../../utils";
import { CardSkeleton } from "../Skeleton";
import { NoteCard } from "../Note";
import TaskCard from "./TaskCard";
import EditJobModal from "./EditJobModal";
import TaskModal from "../../../Modals/TaskModal";
import SlideshowModal from "./SlideshowModal";
import "./StackedView.css";
import { useTaskInfiniteScroll } from "../../hooks/useTaskInfiniteScroll";
import ProjectStack from "./ProjectStack";
import WorkspaceStack from "./WorkspaceStack";
import { StackedViewLoading } from "./StackedViewLoading ";
import CollapsedStackView from "./CollapsedStackView";
import toast from "react-hot-toast";
import Modal from "../../../Modals/Modal";
import AddProjectModal from '../../../Projects/AddProjectModal';
import AddJobModal from '../../../Jobs/AddJobModal';
import JobPermissionManagement from "../../../JobPermission/JobPermissionManagement";
import {
  TASK_STATUSES,
  ALL_TASKS_TAB,
  styles,
  responsiveCSS,
} from "./StackedView.constants";
import { getInitials } from "../../utils/stringUtils";
import { useJobUsers } from "../../hooks/useJobUsers";
import { useJobTaskUsers } from "../../hooks/useJobTaskUsers";
import { useSlideshow } from "../../hooks/useSlideshow";
import { useTaskModals } from "../../hooks/useTaskModals";
import { useTaskHighlighting } from "../../hooks/useTaskHighlighting";
import { usePermissionModal } from "../../hooks/usePermissionModal";
import { useEditJobModal } from "../../hooks/useEditJobModal";
import { useWorkspaceGroups } from "../../hooks/useWorkspaceGroups";

const StackedView = React.memo(
  ({
    stackedJobs,
    loadingStackedJobs,
    expandedStacks,
    toggleStackExpansion,
    hasActiveFilters,
    searchTerm,
    openAiDialogForJob,
    renderStackedImageIcon,
    displayNotes,
    selectedRow,
    handleRowClick,
    handleRowDoubleClick,
    handleAddFromRow,
    handleEdit,
    handleDelete,
    handleViewAttachments,
    handleReplyToNote,
    handlePriorityClick,
    handlePriorityMouseEnter,
    handlePriorityMouseLeave,
    handleNoteTextMouseEnter,
    handleNoteTextMouseLeave,
    handleLinkedNoteClick,
    handleLinkedNoteMouseEnter,
    handleLinkedNoteMouseLeave,
    isNoteReply,
    getReplyNoteId,
    isOriginalNoteExists,
    userStatusMap,
    loadingUsers,
    getPriorityValue,
    manuallyUpdatedPriorities,
    shouldShowNotePopup,
    isStackedViewLoading,
    isFilteringStacked,
    jobsToDisplay,
    onSaveTask,
    userId,
    tasksByJob = {},
    loadingTasksByJob = {},
    expandedTasks = {},
    setExpandedTasks,
    toggleTaskExpansion,
    notesByTask = {},
    loadingNotesByTask = {},
    fetchTasksForJob,
    fetchNotesForTask,
    onTaskEdit,
    onTaskDelete,
    onTaskStatusChange,
    onTaskClick,
    isClearingFilter,
    refreshTasksForJob,
    onFilterByProject,
    onFilterByWorkspace,
    refreshNotes,
    highlightedTaskId = null,
    highlightedNoteId = null,  // ← ADD THIS
    clearHighlightedTask = () => {},
    loadMoreTaskNotes,
    taskPagination,
    workspaceRole,
    expandedProjects,
    setExpandedProjects,
    activeTabByJob,
    handleTabChange,
    assigneeFilters,
    handleAssigneeFilter,
    clearAssigneeFilter,
    workspacesMetadata,            
    onRefreshWorkspacesMetadata,
    selectedFilters,
    expandedWorkspaces,
    setExpandedWorkspaces,
    onPriorityUpdate
  }) => {
    
    const [localExpandedStacks, setLocalExpandedStacks] = useState({});
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const [workspaceStackModal, setWorkspaceStackModal] = useState({
      addProject: { isOpen: false, workspaceId: null, workspaceName: null },
      addJob: { isOpen: false, workspaceId: null, projectId: null, projectName: null },
      createTask: { isOpen: false, jobId: null, jobName: null }
    });

    const { jobUsers, loadingUsersByJob, fetchJobPermissionUsers } =
      useJobUsers(apiUrl);
    const { jobUsersTask, loadingUsersByJobTask, fetchJobTaskUsers } =
      useJobTaskUsers(apiUrl);
    const { highlightedTask, setHighlightedTask, clearHighlight } =
      useTaskHighlighting(highlightedTaskId, clearHighlightedTask);
    const {
      slideshowOpen,
      slideshowNotes,
      slideshowJobName,
      slideshowStartIndex,
      loadingSlideshowJob,
      setSlideshowOpen,
      handleOpenSlideshow,
    } = useSlideshow(apiUrl, userId);

    const {
      taskModalOpen,
      selectedJobForTask,
      isSavingTask,
      editTaskModalOpen,
      taskToEdit,
      selectedJobForTaskEdit,
      isUpdatingTask,
      handleCreateTask,
      handleSaveTask,
      handleTaskEdit,
      handleTaskUpdate,
      closeTaskModal,
      closeEditTaskModal,
    } = useTaskModals({
      onSaveTask,
      onTaskEdit,
      refreshTasksForJob,
      fetchTasksForJob,
      refreshNotes,
    });

    const {
      jobPermissionModalOpen,
      selectedWorkspaceId,
      selectedUserId,
      selectedJobForPermission,
      openPermissionModal,
      closePermissionModal,
    } = usePermissionModal();

    const { editJobModalOpen, jobToEdit, openEditJobModal, closeEditJobModal } =
      useEditJobModal();

    const { workspaces, ungroupedJobs, loadingMetadata } = useWorkspaceGroups(
      stackedJobs,
      userId,
      apiUrl
    );

    const handleTaskEditWrapper = useCallback(
      (task) => {
        const safeTasksByJob = tasksByJob || {};
        const safeStackedJobs = stackedJobs || [];
        handleTaskEdit(task, safeTasksByJob, safeStackedJobs);
      },
      [handleTaskEdit, tasksByJob, stackedJobs],
    );
    
    const handleWorkspaceModal = useCallback((type, data) => {
      setWorkspaceStackModal(prev => ({
        ...prev,
        [type]: { isOpen: true, ...data }
      }));
    }, []);

    const closeWorkspaceModal = useCallback((type) => {
      setWorkspaceStackModal(prev => ({
        ...prev,
        [type]: { ...prev[type], isOpen: false }
      }));
    }, []);
    
    const isAnyProjectExpanded = useMemo(
      () => Object.values(expandedProjects).some((v) => v),
      [expandedProjects],
    );

    const isAnyWorkspaceExpanded = useMemo(
      () => Object.values(expandedWorkspaces).some(v => v),
      [expandedWorkspaces],
    );

    const handleTaskCardClick = useCallback(
      (task) => {
        if (onTaskClick) {
          onTaskClick(task);
        } else if (toggleTaskExpansion) {
          toggleTaskExpansion(task.id, task.jobId);
        }
      },
      [onTaskClick, toggleTaskExpansion],
    );

    useTaskHighlighting(highlightedTaskId, clearHighlightedTask);
    
    const toggleWorkspaceExpansion = useCallback((workspaceId) => {
      setExpandedWorkspaces(prev => ({
        ...prev,
        [workspaceId]: !prev[workspaceId]
      }));
    }, [setExpandedWorkspaces]);
    
    const handleDenyUserClick = openPermissionModal;

    if (isStackedViewLoading || isClearingFilter) {
      return <StackedViewLoading />;
    }
    if (!stackedJobs || stackedJobs.length === 0) return null;

    return (
      <div className="stacked-notes-horizontal">
        <div className="stacked-container">
          {workspaces.map((workspace) => {
            const isWorkspaceExpanded = expandedWorkspaces[workspace.workspaceId];
            if (isAnyWorkspaceExpanded && !isWorkspaceExpanded) return null;
            return (
              <WorkspaceStack
                key={`workspace-${workspace.workspaceId}`}
                workspace={workspace}
                projects={workspace.projects}
                expandedWorkspaces={expandedWorkspaces}
                toggleWorkspaceExpansion={toggleWorkspaceExpansion}
                expandedProjects={expandedProjects}
                toggleProjectExpansion={(projectId) => {
                  setExpandedProjects((prev) => ({
                    ...prev,
                    [projectId]: !prev[projectId],
                  }));
                }}
                stackedJobs={stackedJobs}
                expandedStacks={expandedStacks}
                toggleStackExpansion={toggleStackExpansion}
                searchTerm={searchTerm}
                hasActiveFilters={hasActiveFilters}
                openAiDialogForJob={openAiDialogForJob}
                renderStackedImageIcon={renderStackedImageIcon}
                selectedRow={selectedRow}
                TASK_STATUSES={TASK_STATUSES}
                ALL_TASKS_TAB={ALL_TASKS_TAB}
                handleRowClick={handleRowClick}
                handleRowDoubleClick={handleRowDoubleClick}
                handleAddFromRow={handleAddFromRow}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleViewAttachments={handleViewAttachments}
                handleReplyToNote={handleReplyToNote}
                handlePriorityClick={handlePriorityClick}
                handlePriorityMouseEnter={handlePriorityMouseEnter}
                handlePriorityMouseLeave={handlePriorityMouseLeave}
                handleNoteTextMouseEnter={handleNoteTextMouseEnter}
                handleNoteTextMouseLeave={handleNoteTextMouseLeave}
                handleLinkedNoteClick={handleLinkedNoteClick}
                handleLinkedNoteMouseEnter={handleLinkedNoteMouseEnter}
                handleLinkedNoteMouseLeave={handleLinkedNoteMouseLeave}
                isNoteReply={isNoteReply}
                getReplyNoteId={getReplyNoteId}
                isOriginalNoteExists={isOriginalNoteExists}
                userStatusMap={userStatusMap}
                loadingUsers={loadingUsers}
                getPriorityValue={getPriorityValue}
                manuallyUpdatedPriorities={manuallyUpdatedPriorities}
                shouldShowNotePopup={shouldShowNotePopup}
                userId={userId}
                tasksByJob={tasksByJob}
                jobUsersTask={jobUsersTask}
                loadingUsersByJobTask={loadingUsersByJobTask}
                loadingTasksByJob={loadingTasksByJob}
                expandedTasks={expandedTasks}
                setExpandedTasks={setExpandedTasks}
                toggleTaskExpansion={toggleTaskExpansion}
                notesByTask={notesByTask}
                loadingNotesByTask={loadingNotesByTask}
                fetchTasksForJob={fetchTasksForJob}
                fetchNotesForTask={fetchNotesForTask}
                onTaskEdit={handleTaskEdit}
                onTaskDelete={onTaskDelete}
                onTaskStatusChange={onTaskStatusChange}
                onTaskClick={handleTaskCardClick}
                refreshTasksForJob={refreshTasksForJob}
                onFilterByProject={onFilterByProject}
                onFilterByWorkspace={onFilterByWorkspace}
                refreshNotes={refreshNotes}
                highlightedTaskId={highlightedTaskId}
                highlightedNoteId={highlightedNoteId}  // ← ADD THIS
                clearHighlightedTask={clearHighlightedTask}
                loadMoreTaskNotes={loadMoreTaskNotes}
                taskPagination={taskPagination}
                onOpenEdit={openEditJobModal}
                onCreateTask={handleCreateTask}
                onOpenSlideshow={handleOpenSlideshow}
                activeTabByJob={activeTabByJob}
                onTabChange={handleTabChange}
                assigneeFilters={assigneeFilters}
                jobUsers={jobUsers}
                loadingUsersByJob={loadingUsersByJob}
                onAssigneeFilter={handleAssigneeFilter}
                clearAssigneeFilter={clearAssigneeFilter}
                fetchJobPermissionUsers={fetchJobPermissionUsers}
                workspaceRole={workspaceRole}
                onDenyUserClick={handleDenyUserClick}
                fetchJobTaskUsers={fetchJobTaskUsers}
                onTaskEdit={handleTaskEditWrapper}
                workspacesMetadata={workspacesMetadata}
                onRefreshWorkspacesMetadata={onRefreshWorkspacesMetadata}
                selectedFilters={selectedFilters}
                onPriorityUpdate={onPriorityUpdate} 
                onOpenModal={handleWorkspaceModal}
              />
            );
          })}

          {/* Render ungrouped jobs if any */}
          {ungroupedJobs.length > 0 && (
            <div style={{ marginTop: "20px", borderTop: "1px solid #e0e0e0", paddingTop: "20px" }}>
              <h4>Unassigned Jobs</h4>
              {ungroupedJobs.map(job => (
                <CollapsedStackView
                  key={`ungrouped-job-${job.jobId}`}
                  job={job}
                  searchTerm={searchTerm}
                  hasActiveFilters={hasActiveFilters}
                  openAiDialogForJob={openAiDialogForJob}
                  toggleStackExpansion={toggleStackExpansion}
                  onOpenEdit={openEditJobModal}
                  onCreateTask={handleCreateTask}
                  onOpenSlideshow={handleOpenSlideshow}
                  onFilterByProject={onFilterByProject}
                  onFilterByWorkspace={onFilterByWorkspace}
                  jobUsers={jobUsers}
                  loadingUsersByJob={loadingUsersByJob}
                  fetchJobPermissionUsers={fetchJobPermissionUsers}
                  workspaceRole={workspaceRole}
                  onDenyUserClick={handleDenyUserClick}
                />
              ))}
            </div>
          )}

          {/* Modals - existing modal code remains the same */}
          {editJobModalOpen && jobToEdit && (
            <EditJobModal
              isOpen={editJobModalOpen}
              onClose={closeEditJobModal}
              jobId={jobToEdit.jobId || jobToEdit.id || jobToEdit.jobID}
              workspaceId={
                jobToEdit.workspaceId ||
                jobToEdit.workspace ||
                jobToEdit.defWorkId ||
                null
              }
              projectId={jobToEdit.projectId || jobToEdit.project?.id || null}
              projectName={
                jobToEdit.projectName || jobToEdit.project?.name || ""
              }
              userId={jobToEdit.userId}
              onJobUpdated={() => {
                closeEditJobModal();
              }}
            />
          )}
         
          <AddProjectModal
            isOpen={workspaceStackModal.addProject.isOpen}
            onClose={() => closeWorkspaceModal('addProject')}
            workspaceId={workspaceStackModal.addProject.workspaceId}
            workspaceName={workspaceStackModal.addProject.workspaceName}
            userId={userId}
            existingProjects={workspaceStackModal.addProject.workspaceId 
              ? workspaces.find(w => w.workspaceId === workspaceStackModal.addProject.workspaceId)?.projects || []
              : []
            }
            onAddSuccess={async () => {
              if (onRefreshWorkspacesMetadata) await onRefreshWorkspacesMetadata();
              if (refreshNotes) await refreshNotes();
              closeWorkspaceModal('addProject');
            }}
          />

          <AddJobModal
            isOpen={workspaceStackModal.addJob.isOpen}
            onClose={() => closeWorkspaceModal('addJob')}
            defWorkId={workspaceStackModal.addJob.workspaceId}
            user={userId}
            onJobAdded={async () => {
              if (refreshNotes) await refreshNotes();
              if (onRefreshWorkspacesMetadata) await onRefreshWorkspacesMetadata();
              closeWorkspaceModal('addJob');
            }}
            isAdmin={workspaceRole === 1}
            initialProjectId={workspaceStackModal.addJob.projectId}
            initialProjectName={workspaceStackModal.addJob.projectName}
          />

          <TaskModal
            isOpen={workspaceStackModal.createTask.isOpen}
            onClose={() => closeWorkspaceModal('createTask')}
            jobId={workspaceStackModal.createTask.jobId}
            jobName={workspaceStackModal.createTask.jobName}
            onSaveTask={async (newTask) => {
              if (workspaceStackModal.createTask.jobId && fetchTasksForJob) {
                await fetchTasksForJob(workspaceStackModal.createTask.jobId);
              }
              if (refreshNotes) await refreshNotes();
              closeWorkspaceModal('createTask');
            }}
            mode="create"
            fetchTasksForJob={fetchTasksForJob}
            refreshNotes={refreshNotes}
          />

          {taskModalOpen && selectedJobForTask && (
            <TaskModal
              isOpen={taskModalOpen}
              onClose={closeTaskModal}
              jobId={selectedJobForTask.id}
              jobName={selectedJobForTask.name}
              onSaveTask={handleSaveTask}
              isLoading={isSavingTask}
              mode="create"
              fetchTasksForJob={fetchTasksForJob}
              refreshNotes={refreshNotes}
            />
          )}
          {editTaskModalOpen && selectedJobForTaskEdit && taskToEdit && (
            <TaskModal
              isOpen={editTaskModalOpen}
              onClose={closeEditTaskModal}
              jobId={selectedJobForTaskEdit.id}
              jobName={selectedJobForTaskEdit.name}
              onSaveTask={handleTaskUpdate}
              isLoading={isUpdatingTask}
              taskToEdit={taskToEdit}
              mode="edit"
              fetchTasksForJob={fetchTasksForJob}
              refreshNotes={refreshNotes}
            />
          )}

          <SlideshowModal
            key={`${slideshowJobName}-${slideshowNotes[0]?.id || "no-notes"}`}
            isOpen={slideshowOpen}
            onClose={() => setSlideshowOpen(false)}
            notes={slideshowNotes}
            currentNoteIndex={slideshowStartIndex}
            jobName={slideshowJobName}
            showTaskName={true}
          />

          {jobPermissionModalOpen && selectedWorkspaceId && selectedUserId && (
            <Modal
              isOpen={jobPermissionModalOpen}
              onClose={closePermissionModal}
              title={`Manage Job Permissions - ${selectedJobForPermission?.jobName || "Job"}`}
            >
              <JobPermissionManagement
                defId={selectedWorkspaceId}
                users={[selectedUserId]}
                userId={selectedUserId}
                jobId={selectedJobForPermission?.jobId}
                defaultTab="userJobs"
              />
            </Modal>
          )}
        </div>
      </div>
    );
  },
);

StackedView.propTypes = {
  stackedJobs: PropTypes.array,
  loadingStackedJobs: PropTypes.bool,
  expandedStacks: PropTypes.object.isRequired,
  toggleStackExpansion: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool,
  searchTerm: PropTypes.string,
  openAiDialogForJob: PropTypes.func.isRequired,
  renderStackedImageIcon: PropTypes.func,
  displayNotes: PropTypes.array,
  selectedRow: PropTypes.any,
  handleRowClick: PropTypes.func,
  handleRowDoubleClick: PropTypes.func,
  handleAddFromRow: PropTypes.func,
  handleEdit: PropTypes.func,
  handleDelete: PropTypes.func,
  handleViewAttachments: PropTypes.func,
  handleReplyToNote: PropTypes.func,
  handlePriorityClick: PropTypes.func,
  handlePriorityMouseEnter: PropTypes.func,
  handlePriorityMouseLeave: PropTypes.func,
  handleNoteTextMouseEnter: PropTypes.func,
  handleNoteTextMouseLeave: PropTypes.func,
  handleLinkedNoteClick: PropTypes.func,
  handleLinkedNoteMouseEnter: PropTypes.func,
  handleLinkedNoteMouseLeave: PropTypes.func,
  isNoteReply: PropTypes.func,
  getReplyNoteId: PropTypes.func,
  isOriginalNoteExists: PropTypes.func,
  userStatusMap: PropTypes.object,
  loadingUsers: PropTypes.object,
  getPriorityValue: PropTypes.func,
  manuallyUpdatedPriorities: PropTypes.object,
  shouldShowNotePopup: PropTypes.func,
  isStackedViewLoading: PropTypes.bool,
  isFilteringStacked: PropTypes.bool,
  jobsToDisplay: PropTypes.array,
  onSaveTask: PropTypes.func,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  expandedTasks: PropTypes.object,
  toggleTaskExpansion: PropTypes.func,
  notesByTask: PropTypes.object,
  loadingNotesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  onTaskEdit: PropTypes.func,
  onTaskDelete: PropTypes.func,
  onTaskStatusChange: PropTypes.func,
  onTaskClick: PropTypes.func,
  refreshTasksForJob: PropTypes.func,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  highlightedNoteId: PropTypes.number,  // ← ADD THIS
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  workspaceRole: PropTypes.number,
  expandedProjects: PropTypes.object,
  setExpandedProjects: PropTypes.func,
  activeTabByJob: PropTypes.object,
  handleTabChange: PropTypes.func,
  assigneeFilters: PropTypes.object,
  handleAssigneeFilter: PropTypes.func,
  clearAssigneeFilter: PropTypes.func,
  setExpandedTasks: PropTypes.func,
  workspacesMetadata: PropTypes.array,
  onRefreshWorkspacesMetadata: PropTypes.func,
  selectedFilters: PropTypes.object,
  expandedWorkspaces: PropTypes.object,
  setExpandedWorkspaces: PropTypes.func,
  onPriorityUpdate: PropTypes.func,
};

StackedView.defaultProps = {
  stackedJobs: [],
  loadingStackedJobs: false,
  hasActiveFilters: false,
  searchTerm: "",
  renderStackedImageIcon: () => null,
  displayNotes: [],
  selectedRow: null,
  isStackedViewLoading: false,
  isFilteringStacked: false,
  jobsToDisplay: [],
  isOriginalNoteExists: () => false,
  onSaveTask: () => {},
  tasksByJob: {},
  loadingTasksByJob: {},
  expandedTasks: {},
  toggleTaskExpansion: () => {},
  notesByTask: {},
  loadingNotesByTask: {},
  fetchTasksForJob: () => {},
  fetchNotesForTask: () => {},
  onTaskEdit: () => {},
  onTaskDelete: () => {},
  onTaskStatusChange: () => {},
  onTaskClick: () => {},
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  highlightedTaskId: null,
  highlightedNoteId: null,  // ← ADD THIS
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  expandedProjects: {},
  setExpandedProjects: () => {},
  activeTabByJob: {},
  handleTabChange: () => {},
  assigneeFilters: {},
  handleAssigneeFilter: () => {},
  clearAssigneeFilter: () => {},
  setExpandedTasks: () => {},
  workspacesMetadata: [],
  onRefreshWorkspacesMetadata: () => {},
  selectedFilters: {},
  expandedWorkspaces: {},
  setExpandedWorkspaces: () => {},
  onPriorityUpdate: () => {},
};

if (
  typeof document !== "undefined" &&
  !document.getElementById("stacked-view-responsive-styles")
) {
  const styleElement = document.createElement("style");
  styleElement.id = "stacked-view-responsive-styles";
  styleElement.textContent = responsiveCSS;
  document.head.appendChild(styleElement);
}

export default StackedView;